import { LwFunnel, type LwFunnelStep } from '@/components/lw';
import type { RecruitmentCandidate } from './types';
import type { RecruitStatus } from '@/types/recruitment';

interface RecruitmentPipelineFunnelProps {
  candidates: RecruitmentCandidate[];
  /** current status filter ('all' when none) */
  activeStatus: string;
  onSelectStatus: (status: string) => void;
}

// The candidate lifecycle, ending in the gold "goal" step. On Hold / Denied /
// Terminated are off-funnel and surfaced as KPIs.
const FUNNEL: { status: RecruitStatus; stage: string; label: string; sub: string; goal?: boolean }[] = [
  { status: 'Inquiry', stage: 'inquiry', label: 'Inquiry', sub: 'new leads' },
  { status: 'Pending', stage: 'pending', label: 'Pending', sub: 'in review' },
  { status: 'In Progress', stage: 'in_progress', label: 'In Progress', sub: 'active' },
  { status: 'Approved', stage: 'approved', label: 'Approved', sub: 'onboarding', goal: true }
];

export function RecruitmentPipelineFunnel({
  candidates,
  activeStatus,
  onSelectStatus
}: RecruitmentPipelineFunnelProps) {
  const counts = candidates.reduce<Record<string, number>>((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisMonth = candidates.filter((c) => new Date(c.createdAt) >= firstOfMonth).length;

  const steps: LwFunnelStep[] = FUNNEL.map((s) => ({
    key: s.status,
    stage: s.stage,
    label: s.label,
    count: counts[s.status] || 0,
    sub: s.sub,
    goal: s.goal,
    active: activeStatus === s.status,
    // Toggle: clicking the active step clears the filter back to 'all'.
    onClick: () => onSelectStatus(activeStatus === s.status ? 'all' : s.status)
  }));

  const kpis = [
    { value: counts['On Hold'] || 0, label: 'on hold' },
    { value: counts['Denied'] || 0, label: 'denied' },
    { value: thisMonth, label: 'added this month' }
  ];

  return <LwFunnel steps={steps} kpis={kpis} />;
}
